'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Button } from '@/components/Button';
import { usePopoverPosition } from '@/hooks/usePopoverPosition';
import { usePortalTarget } from '@/components/PortalTarget';

// Enable dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Types
interface DateRange {
  from: Date | null;
  to: Date | null;
}

export type TimeRangeValue =
  | 'today'
  | 'last-7-days'
  | 'last-30-days'
  | 'last-3-months'
  | 'all-time'
  | 'custom';

interface TimeRangeOption {
  label: string;
  value: TimeRangeValue;
}

interface DateRangeFilterProps {
  /** `timeRange` names the preset that produced the range ('custom' for a picked one). */
  onDateRangeChange?: (range: DateRange, timeRange: TimeRangeValue) => void;
  className?: string;
  /** Overrides the range `initialTimeRange` would resolve to. */
  initialRange?: DateRange;
  /**
   * Which preset the dropdown opens on, and — unless `initialRange` overrides
   * it — the range shown for it. The parent still owns the data and fetches
   * that same window itself on mount: this control fires `onDateRangeChange`
   * on interaction, never on render.
   */
  initialTimeRange?: TimeRangeValue;
}

// Time range options
const timeRangeOptions: TimeRangeOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'Last 3 Months', value: 'last-3-months' },
  { label: 'All Time', value: 'all-time' },
  { label: 'Custom', value: 'custom' },
];

// The month grid IS the picker — the bounds beside it are read-only text
const Calendar: React.FC<{
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  showDualCalendar?: boolean;
}> = ({
  range,
  onRangeChange,
  currentMonth,
  onMonthChange,
  showDualCalendar = true,
}) => {
  const handlePrevMonth = () => {
    const prevMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    );
    onMonthChange(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    );
    onMonthChange(nextMonth);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Month Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <button
          onClick={handlePrevMonth}
          className="p-1 rounded hover:bg-gray-100"
        >
          <i className="ri-arrow-left-s-line text-gray-600"></i>
        </button>

        <span className="font-medium text-gray-900">
          {dayjs(currentMonth).format('MMMM YYYY')}
        </span>

        <button
          onClick={handleNextMonth}
          className="p-1 rounded hover:bg-gray-100"
        >
          <i className="ri-arrow-right-s-line text-gray-600"></i>
        </button>
      </div>

      {/* The picked bounds, read-only. They used to be `<input type="date">`,
          which opened the BROWSER's own date picker on top of the month grid
          below — two calendars in one panel, and clicking either one fought the
          other. The grid is the picker; these only report what it holds. */}
      <div className="flex-1 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </span>
            <p className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-900">
              {range.from
                ? dayjs(range.from).format('DD MMM YYYY')
                : 'Pilih tanggal'}
            </p>
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </span>
            <p className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-900">
              {range.to
                ? dayjs(range.to).format('DD MMM YYYY')
                : 'Pilih tanggal'}
            </p>
          </div>
        </div>

        {/* The one picker in this panel: first click sets the start, second the
            end. */}
        <div className="mt-6">
          <div className="text-xs text-gray-500 mb-2">
            Klik tanggal awal, lalu tanggal akhir
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="font-medium text-gray-400 p-2">
                {day}
              </div>
            ))}
            {/* Leading blanks so the 1st lands under its real weekday — the
                grid is the picker now, and a shifted column reads as the wrong
                date. */}
            {Array.from(
              { length: dayjs(currentMonth).startOf('month').day() },
              (_, i) => (
                <div key={`pad-${i}`} aria-hidden />
              )
            )}
            {Array.from(
              { length: dayjs(currentMonth).daysInMonth() },
              (_, i) => {
                const date = dayjs(currentMonth)
                  .date(i + 1)
                  .toDate();
                const isInRange =
                  range.from &&
                  range.to &&
                  date >= range.from &&
                  date <= range.to;
                const isStart =
                  range.from && dayjs(date).isSame(range.from, 'day');
                const isEnd = range.to && dayjs(date).isSame(range.to, 'day');

                return (
                  <button
                    key={i}
                    onClick={() => {
                      // First click (or a click after a complete range) starts
                      // over; the second closes the range, ordered so clicking
                      // backwards picks a range rather than an empty one.
                      if (!range.from || range.to) {
                        onRangeChange({ from: date, to: null });
                        return;
                      }
                      const [from, to] =
                        date >= range.from
                          ? [range.from, date]
                          : [date, range.from];
                      onRangeChange({ from, to });
                    }}
                    className={`
                    p-2 text-xs rounded
                    ${isStart || isEnd ? 'bg-blue-500 text-white' : ''}
                    ${isInRange ? 'bg-blue-100 text-blue-900' : ''}
                    ${
                      !isInRange && !isStart && !isEnd
                        ? 'hover:bg-gray-100'
                        : ''
                    }
                  `}
                  >
                    {i + 1}
                  </button>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// A local date at start of day, `daysFromToday` away.
const createLocalDate = (daysFromToday: number = 0): Date => {
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + daysFromToday,
    0,
    0,
    0,
    0
  );
};

/**
 * The range a preset means. At module scope so the component can resolve its
 * initial preset exactly the way a click on that preset resolves it — one
 * definition of what "Today" is, instead of every caller restating the dates
 * beside the preset name.
 */
const rangeForTimeOption = (option: TimeRangeValue): DateRange => {
  switch (option) {
    case 'today':
      return { from: createLocalDate(0), to: createLocalDate(0) };
    case 'last-7-days':
      return { from: createLocalDate(-7), to: createLocalDate(0) };
    case 'last-30-days':
      return { from: createLocalDate(-30), to: createLocalDate(0) };
    case 'last-3-months':
      return {
        from: dayjs().subtract(3, 'month').startOf('day').toDate(),
        to: createLocalDate(0),
      };
    case 'all-time':
    default:
      return { from: null, to: null };
  }
};

const CALENDAR_PANEL = {
  width: 400,
  minHeight: 610,
  align: 'right' as const,
};

const PRESET_PANEL = {
  width: 192,
  // Diturunkan dari daftarnya, bukan angka hafalan: 36px per baris + padding.
  minHeight: timeRangeOptions.length * 36 + 8,
  align: 'left' as const,
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  className = '',
  initialRange,
  initialTimeRange = 'all-time',
}) => {
  // The <dialog> node when rendered inside an open SideBarModal, else body.
  const portalTarget = usePortalTarget();
  // The preset resolves to its own range unless the caller overrides it: a
  // screen names a window ("today"), it does not restate the dates.
  const [range, setRange] = useState<DateRange>(
    () => initialRange ?? rangeForTimeOption(initialTimeRange)
  );
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<TimeRangeValue>(initialTimeRange);
  const [isTimeRangeOpen, setIsTimeRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const timeRangeRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const { panelRef, placement: panelPos } = usePopoverPosition(
    datePickerRef,
    isDatePickerOpen,
    CALENDAR_PANEL
  );
  const { panelRef: presetRef, placement: presetPos } = usePopoverPosition(
    timeRangeRef,
    isTimeRangeOpen,
    PRESET_PANEL
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        timeRangeRef.current &&
        !timeRangeRef.current.contains(event.target as Node) &&
        !presetRef.current?.contains(event.target as Node)
      ) {
        setIsTimeRangeOpen(false);
      }
      // Panelnya di-portal ke body, jadi bukan turunan datePickerRef lagi:
      // tanpa memeriksa panelRef, klik di dalam kalender menutupnya sendiri.
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        !panelRef.current?.contains(event.target as Node)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelRef, presetRef]);

  // Removed automatic range change notification - only on Apply

  const handleTimeRangeSelect = (option: TimeRangeValue) => {
    setSelectedTimeRange(option);
    setIsTimeRangeOpen(false);

    if (option === 'all-time') {
      const newRange = { from: null, to: null };
      setRange(newRange);
      onDateRangeChange?.(newRange, option);
      return;
    }

    if (option === 'custom') {
      setIsDatePickerOpen(true);
      return;
    }

    // Presets apply immediately — they ARE the choice. Routing them through
    // the picker's Apply button left the parent never notified: the option
    // looked selected while the data silently kept its previous range.
    const newRange = rangeForTimeOption(option);
    setRange(newRange);
    if (newRange.from) {
      setCurrentMonth(newRange.from);
    } else {
      setCurrentMonth(new Date());
    }
    onDateRangeChange?.(newRange, option);
  };

  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    setRange(newRange);
    // Reset time range selection when manually selecting dates
    if (newRange.from || newRange.to) {
      setSelectedTimeRange('custom');
    }
  }, []);

  const handleApplyDateRange = () => {
    setIsDatePickerOpen(false);
    onDateRangeChange?.(range, selectedTimeRange);
  };

  const handleClearDateRange = () => {
    const clearedRange = { from: null, to: null };
    setRange(clearedRange);
    setSelectedTimeRange('all-time');
    onDateRangeChange?.(clearedRange, 'all-time');
  };

  const formatDateRange = (range: DateRange): string => {
    if (!range.from) return 'Select date range';

    if (!range.to) {
      return dayjs(range.from).format('MMM DD, YYYY');
    }

    if (dayjs(range.from).isSame(range.to, 'day')) {
      return dayjs(range.from).format('MMM DD, YYYY');
    }

    return `${dayjs(range.from).format('MMM DD')} - ${dayjs(range.to).format(
      'MMM DD, YYYY'
    )}`;
  };

  const getSelectedTimeRangeLabel = () => {
    const option = timeRangeOptions.find(
      (opt) => opt.value === selectedTimeRange
    );
    return option?.label || 'All Time';
  };

  return (
    <div className={`inline-flex ${className}`}>
      {/* Time Range Dropdown */}
      <div className="relative" ref={timeRangeRef}>
        <button
          onClick={() => setIsTimeRangeOpen(!isTimeRangeOpen)}
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-l-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <span className="truncate max-w-32">
            {getSelectedTimeRangeLabel()}
          </span>
          <i
            className={`ri-arrow-down-s-line transition-transform ${
              isTimeRangeOpen ? 'rotate-180' : ''
            }`}
          ></i>
        </button>

        {isTimeRangeOpen &&
          createPortal(
            <div
              ref={presetRef}
              className="fixed z-[80] bg-white border border-gray-300 rounded-md shadow-lg"
              style={{
                width: `${PRESET_PANEL.width}px`,
                top: presetPos.top,
                left: presetPos.left,
              }}
            >
              <div className="p-1">
                {timeRangeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTimeRangeSelect(option.value)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedTimeRange === option.value
                        ? 'bg-blue-100 text-blue-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>,
            portalTarget
          )}
      </div>

      {/* Date Picker Button */}
      <div className="relative" ref={datePickerRef}>
        <button
          onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          className="flex items-center gap-2 px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <i className="ri-calendar-line text-gray-600"></i>
          <span className="truncate max-w-48">
            {selectedTimeRange === 'custom'
              ? formatDateRange(range)
              : 'Custom Range'}
          </span>
        </button>

        {isDatePickerOpen &&
          createPortal(
            <div
              ref={panelRef}
              // Di-portal ke body dan `fixed`, bukan `absolute` di dalam
              // trigger-nya: Layout membungkus isi halaman dengan
              // `overflow-hidden` dan `overflow-auto`, yang memotong popover
              // apa pun yang melewati tepinya. Delapan halaman menaruh filter
              // ini di blok kiri, jadi panel 400px-nya memanjang ke kiri
              // melewati batas itu dan kolom hari pertamanya hilang.
              className="fixed z-[80] bg-white border border-gray-300 rounded-lg shadow-lg"
              style={{
                width: `${CALENDAR_PANEL.width}px`,
                minHeight: `${CALENDAR_PANEL.minHeight}px`,
                top: panelPos.top,
                left: panelPos.left,
              }}
            >
              <div className="flex flex-col h-full rounded-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-900">
                    Select Date Range
                  </h3>
                  <button
                    onClick={() => setIsDatePickerOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                {/* Calendar */}
                <Calendar
                  range={range}
                  onRangeChange={handleDateRangeChange}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                />

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    {range.from && range.to
                      ? `${dayjs(range.from).format('MMM DD, YYYY')} - ${dayjs(
                          range.to
                        ).format('MMM DD, YYYY')}`
                      : range.from
                        ? dayjs(range.from).format('MMM DD, YYYY')
                        : 'No range selected'}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      variant="neutral"
                      onClick={handleClearDateRange}
                    >
                      Clear
                    </Button>
                    <Button
                      size="small"
                      variant="primary"
                      onClick={handleApplyDateRange}
                      disabled={!range.from}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </div>,
            portalTarget
          )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
